"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight, Check } from "lucide-react";
import Link from "next/link";

const CYCLE_MS = 6500;
const SYNC_AT = 4500;

export default function MenuBoardsHero() {
  const { t } = useLang();
  const [phase, setPhase] = useState<"editing" | "syncing" | "synced">("editing");
  const [secondsLeft, setSecondsLeft] = useState(2.0);

  // Cycle: editing (0-3s) → counter (3-4.5s, ticks 2.0 to 0.0) → synced (4.5-6.5s) → reset
  useEffect(() => {
    let frame: number;
    const start = performance.now();
    const tick = (now: number) => {
      const t = (now - start) % CYCLE_MS;
      if (t < 3000) {
        setPhase("editing");
        setSecondsLeft(2.0);
      } else if (t < SYNC_AT) {
        setPhase("syncing");
        const progress = (t - 3000) / (SYNC_AT - 3000); // 0..1
        setSecondsLeft(Number((2.0 * (1 - progress)).toFixed(1)));
      } else {
        setPhase("synced");
        setSecondsLeft(0.0);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  // What the TV board shows (sync lags the iPad edit by 2s)
  const tvShowsNewPrice = phase === "synced";

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#FAFAF8]">
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(74,122,232,0.10) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#D7263D" }}
        >
          {t("Live menu boards · Syncs in seconds", "Menu boards en vivo · Sync en segundos")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[40px] sm:text-[56px] md:text-[80px] lg:text-[96px] font-black leading-[0.98] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t("Edit here.", "Editas aquí.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("Refreshes there.", "Refresca allá.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[680px] mx-auto leading-relaxed mb-12"
        >
          {t(
            "Change a price on the POS — the TV menu board catches up in seconds. No designer, no InDesign, no printing, no apologizing for the special that ran out two hours ago.",
            "Cambias un precio en el POS — el menu board de la TV se actualiza en segundos. Sin diseñador, sin InDesign, sin impresión, sin pedir perdón por la oferta que se agotó hace 2 horas."
          )}
        </motion.p>

        {/* Hero: iPad (editing) + TV menu board (catches up) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white border border-black/5 rounded-[32px] p-6 md:p-8 max-w-[1080px] mx-auto shadow-[0_30px_80px_rgba(0,0,0,0.06)] mb-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1.6fr] gap-6 items-center">
            {/* iPad editor (left) */}
            <div className="flex justify-center">
              <IPadFrame>
                <div className="h-full flex flex-col bg-[#FAFAF8] text-redy-charcoal">
                  {/* Status */}
                  <div className="h-5 flex items-center justify-end px-3 text-[8px] font-bold">5G</div>
                  {/* Header */}
                  <div className="px-3 py-2 border-b border-black/5 bg-white">
                    <div className="text-[8px] font-black tracking-widest uppercase text-redy-secondary/55">
                      {t("Menu editor", "Editor menú")}
                    </div>
                    <div className="text-[12px] font-black text-redy-charcoal">Birria Plate</div>
                  </div>

                  {/* Editing field */}
                  <div className="flex-1 px-3 py-4">
                    <div className="text-[8px] font-bold uppercase tracking-wider text-redy-secondary/55 mb-1.5">
                      {t("Price", "Precio")}
                    </div>
                    <div
                      className={`rounded-[12px] px-3 py-3 border-2 transition-all ${
                        phase === "editing" ? "" : "border-black/5 bg-[#FAFAF8]"
                      }`}
                      style={
                        phase === "editing"
                          ? { borderColor: "#F4722B", backgroundColor: "rgba(244,114,43,0.06)" }
                          : undefined
                      }
                    >
                      <div className="flex items-baseline gap-1">
                        <span className="text-[16px] text-redy-secondary">$</span>
                        <motion.span
                          key={phase}
                          initial={{ y: -4, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          className="text-[28px] font-black text-redy-charcoal tabular-nums leading-none"
                        >
                          10.00
                        </motion.span>
                        {phase === "editing" && (
                          <motion.span
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ duration: 0.8, repeat: Infinity }}
                            className="w-0.5 h-6 ml-1 inline-block"
                            style={{ backgroundColor: "#F4722B" }}
                          />
                        )}
                      </div>
                      <div className="text-[8px] text-redy-secondary/55 mt-1 line-through">
                        was $9.00
                      </div>
                    </div>

                    {/* Save button state */}
                    <motion.div
                      key={phase}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className={`mt-3 rounded-[10px] py-2 text-center text-[10px] font-bold text-white`}
                      style={{
                        background:
                          phase === "synced"
                            ? "#2ECC71"
                            : "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                      }}
                    >
                      {phase === "synced" ? (
                        <>
                          <Check className="w-3 h-3 inline mr-1" strokeWidth={3} />
                          {t("Saved", "Guardado")}
                        </>
                      ) : (
                        t("Save price", "Guardar precio")
                      )}
                    </motion.div>
                  </div>
                </div>
              </IPadFrame>
            </div>

            {/* Sync indicator (middle) */}
            <div className="flex md:flex-col items-center gap-2">
              <AnimatePresence mode="wait">
                <motion.div
                  key={phase}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.25 }}
                  className="flex flex-col items-center gap-1.5"
                >
                  {phase === "editing" && (
                    <>
                      <div className="w-8 h-8 rounded-full bg-redy-secondary/10 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-redy-secondary/40" />
                      </div>
                      <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/55">
                        {t("Editing", "Editando")}
                      </div>
                    </>
                  )}
                  {phase === "syncing" && (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{
                          background: "conic-gradient(from 0deg, #F4722B 0%, transparent 75%)",
                        }}
                      >
                        <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center">
                          <span
                            className="text-[10px] font-black tabular-nums"
                            style={{ color: "#F4722B" }}
                          >
                            {secondsLeft.toFixed(1)}
                          </span>
                        </div>
                      </motion.div>
                      <div
                        className="text-[9px] font-bold uppercase tracking-wider"
                        style={{ color: "#F4722B" }}
                      >
                        {t("Syncing", "Sincronizando")}
                      </div>
                    </>
                  )}
                  {phase === "synced" && (
                    <>
                      <motion.div
                        initial={{ scale: 0.5 }}
                        animate={{ scale: 1 }}
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: "#2ECC71" }}
                      >
                        <Check className="w-5 h-5 text-white" strokeWidth={3} />
                      </motion.div>
                      <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#2ECC71" }}>
                        {t("Live", "En vivo")}
                      </div>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* TV menu board (right) */}
            <div>
              <TVFrame>
                <div className="h-full flex flex-col bg-redy-charcoal text-white relative overflow-hidden">
                  <div
                    className="absolute inset-0 opacity-30 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(circle at 70% 20%, rgba(244,114,43,0.3) 0%, transparent 60%)",
                    }}
                  />

                  <div className="relative h-full flex flex-col p-4">
                    <div className="text-[10px] font-black uppercase tracking-widest text-white/55 mb-2">
                      Pepitos · {t("Today's menu", "Menú de hoy")}
                    </div>

                    <div className="grid grid-cols-2 gap-2 flex-1 content-start">
                      {[
                        { name: "Birria Plate", price: tvShowsNewPrice ? "10.00" : "9.00", highlight: true },
                        { name: "Al Pastor", price: "4.00" },
                        { name: "Quesabirria", price: "12.00" },
                        { name: "Carnitas", price: "4.00" },
                      ].map((item, i) => (
                        <div
                          key={item.name}
                          className={`bg-white/[0.06] border border-white/10 rounded-md p-2 ${
                            item.highlight ? "" : ""
                          }`}
                        >
                          <div className="text-[10px] font-bold text-white truncate">{item.name}</div>
                          <div className="flex items-baseline gap-0.5">
                            <span className="text-[10px] text-white/55">$</span>
                            <AnimatePresence mode="wait">
                              <motion.span
                                key={item.price}
                                initial={item.highlight ? { y: -6, opacity: 0 } : false}
                                animate={item.highlight ? { y: 0, opacity: 1 } : undefined}
                                exit={item.highlight ? { y: 6, opacity: 0 } : undefined}
                                transition={{ duration: 0.3 }}
                                className={`text-[18px] font-black tabular-nums ${
                                  item.highlight && tvShowsNewPrice ? "" : "text-white"
                                }`}
                                style={
                                  item.highlight && tvShowsNewPrice
                                    ? {
                                        background:
                                          "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                                        WebkitBackgroundClip: "text",
                                        backgroundClip: "text",
                                        color: "transparent",
                                      }
                                    : undefined
                                }
                              >
                                {item.price}
                              </motion.span>
                            </AnimatePresence>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Toast when synced */}
                    <AnimatePresence>
                      {phase === "synced" && (
                        <motion.div
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: 20, opacity: 0 }}
                          className="mt-2 rounded-md px-2 py-1.5 flex items-center justify-between text-[9px] font-bold"
                          style={{
                            backgroundColor: "rgba(46,204,113,0.15)",
                            border: "1px solid rgba(46,204,113,0.35)",
                            color: "#2ECC71",
                          }}
                        >
                          <span>{t("Updated on 4 screens", "Actualizado en 4 pantallas")}</span>
                          <span className="text-white/45 tabular-nums">2.0s</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </TVFrame>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center"
        >
          <Link
            href="/signup"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              boxShadow: "0 20px 60px rgba(244, 114, 43, 0.4)",
            }}
          >
            {t("Light up your menu boards →", "Enciende tus menu boards →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function IPadFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-[190px] h-[280px] bg-[#0a0a0a] rounded-[16px] p-[6px] shadow-[0_30px_60px_rgba(0,0,0,0.15)]">
      <div className="w-full h-full rounded-[10px] overflow-hidden bg-white relative">
        {children}
      </div>
    </div>
  );
}

function TVFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative">
      <div className="bg-[#0a0a0a] rounded-[8px] p-[8px] shadow-[0_30px_60px_rgba(0,0,0,0.18)]">
        <div className="aspect-[16/10] rounded-[4px] overflow-hidden bg-redy-charcoal">
          {children}
        </div>
      </div>
      <div className="h-3 bg-[#0a0a0a] mx-auto" style={{ width: "20%" }} />
      <div className="h-1.5 bg-[#0a0a0a] rounded-b-[4px] mx-auto" style={{ width: "40%" }} />
    </div>
  );
}
